import { inject } from '@angular/core';
import { catchError, defer, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { BRAND_CONFIG } from '@core/injection-tokens';
import {
  AuthService,
  LoaderService,
  LogService,
  MsalInitService,
  MultilingualService,
  TokenService,
} from '@core/services';
import { AppConstants } from '@root/app/app.constants';
import { MatDialog } from '@angular/material/dialog';
import { CommonConfirmationPopupComponent } from '@shared/components';

export const MSALGuardCustom: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const brandConfig = inject(BRAND_CONFIG);
  const authService = inject(AuthService);

  // In case, member login after forgot password flow, id_token will be present in URL
  const fragment = route.fragment!;
  const idToken = new URLSearchParams(fragment).get(AppConstants.ID_TOKEN) ?? '';
  const ssoToken = new URLSearchParams(fragment).get(AppConstants.SSO_TOKEN) ?? '';

  const multilingualService = inject(MultilingualService);
  const msal = inject(MsalInitService);
  const logger = inject(LogService);
  const loader = inject(LoaderService);
  const tokenService = inject(TokenService);
  const matDialog = inject(MatDialog);

  return defer(() => {
    logger.log('[MSALGuardCustom] Guard Called');
    if (authService.isAuthorizedByAzureB2c || authService.isAuthorizedBySSO) {
      // if member is authroized already - allow navigation to home page
      multilingualService.getLangMap(multilingualService.getUserLanguage(), true);
      return of(true);
    } else if (ssoToken) {
      // if member is logged via SSO - allow navigation to home page with SSO JWT Token
      authService.login('', ssoToken);
      multilingualService.getLangMap(multilingualService.getUserLanguage(), true);
      return of(true);
    } else if (idToken) {
      logger.log('[MSALGuardCustom] Forgot Password Login');
      // if member has logged in via forgot password flow,
      // navigate member back to login screen upon showing toaster message
      if (tokenService.isForgotPasswordLogin(idToken, brandConfig)) {
        const userMessage = 'Password reset successful. Please re-login with your new password.';
        return openDialog(userMessage, true, matDialog).pipe(
          tap(() => authService.navigateToLoginPage())
        );
      } else {
        return of(false);
      }
    } else if (!authService.isAuthorizedByAzureB2c) {
      // Authorize against in adb2c using MSAL
      logger.log('[MSALGuardCustom] Authorize against in ADB2C using MSAL');
      return authorizeAgainstADB2C(
        authService,
        multilingualService,
        msal,
        logger,
        loader,
        matDialog
      );
    } else {
      return of(false);
    }
  });
};

export function authorizeAgainstADB2C(
  authService: AuthService,
  multilingualService: MultilingualService,
  msal: MsalInitService,
  logger: LogService,
  loader: LoaderService,
  matDialog: MatDialog
): Observable<boolean> {
  loader.show();
  return msal.redirectResult$.pipe(
    take(1),
    switchMap((response) => {
      logger.log('[MSALGuardCustom] After handleRedirectPromise()');
      if (response?.account) {
        msal.msalInstance.setActiveAccount(response.account);
        logger.log('[MSALGuardCustom] Active account set:', msal.msalInstance.getActiveAccount());

        // Acquire token for backend app registration
        return authService.acquireBackendToken().pipe(
          map((tokenResponse) => {
            loader.hide();
            const accessToken = tokenResponse.accessToken;
            logger.log('[MSALGuardCustom] Token:', accessToken);
            authService.login(accessToken, '');
            multilingualService.getLangMap(multilingualService.getUserLanguage(), true);
            return true;
          })
        );
      } else {
        logger.warn('[MSALGuardCustom] No active account. Redirecting to login...');
        authService.navigateToLoginPage();
        return of(false);
      }
    }),
    catchError((error) => {
      loader.hide();
      logger.error('[MSALGuardCustom] handleRedirectPromise() threw an error', error);
      if (error.errorMessage?.includes(AppConstants.ADB2C_ERROR_CODE.FORGOT_PASSWORD)) {
        logger.warn(
          '[MSALGuardCustom] Forgot password triggered, redirecting to password reset flow'
        );
        // Redirect to password reset policy
        authService.navigatetoForgotPasswordFlow();
        return of(false);
      } else if (error.errorCode === 'server_error') {
        // Login API failed with server error.
        // Show dialog with error message
        return loginAPIError(error, matDialog, authService, loader);
      } else {
        // Redirect to login page for other errors
        logger.error('[MSALGuardCustom] handleRedirectPromise error:', error);
        authService.navigateToLoginPage();
        return of(false);
      }
    })
  );
}

function openDialog(
  userMessage: string,
  isSuccess: boolean,
  matDialog: MatDialog,
  isError = false
): Observable<boolean> {
  const dialog = matDialog.open(CommonConfirmationPopupComponent, {
    data: {
      msg: userMessage,
      title: isSuccess ? 'SUCCESS' : 'ERROR',
      confirmButtonText: 'OK',
      showCancelButton: false,
      isError: isError,
    },
  });

  return dialog.afterClosed().pipe(
    take(1),
    map(() => false)
  );
}

function loginAPIError(
  error: any,
  matDialog: MatDialog,
  authService: AuthService,
  loader: LoaderService
) {
  const fullMessage = error?.errorMessage ?? 'Sorry, something went wrong';
  // Remove additonal details like correlation ID, Timestamp from the B2C error message
  const userMessage = fullMessage
    .replace(/AADB2C: /g, '')
    .replace(/Correlation ID: [\w-]{1,100}/g, '')
    .replace(/Timestamp: \d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}Z/g, '')
    .trim();

  return openDialog(userMessage, false, matDialog, true).pipe(
    map(() => {
      loader.show();
      authService.logout();
      return false;
    })
  );
}
